/*
 * ====================================================================================
 * FILE: LogPage.jsx
 * PURPOSE: A dedicated page for exploring historical machine data (logs).
 * 
 * DESCRIPTION:
 * This page allows users to drill down into the history of a specific asset.
 * It has two main modes:
 * 1. Calendar Mode (List): Shows a list of dates that have data available.
 * 2. Detail Mode: Shows every single telemetry packet received on a specific date.
 * 
 * FEATURES:
 * - Date Selection: Users pick a date to investigate.
 * - Detailed Inspection: Every sensor value sent by the machine is visible here.
 * - Export: PDF and Excel downloads for reporting.
 * - Daily Summaries: Automatically calculates total hours run and fuel used for the day.
 * 
 * AUTHOR: Development Team
 * DATE: 2024
 * ====================================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getContractOrigin } from '../../utils/contractOrigin'; // URL management

// --- UI Components (Material UI) ---
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    Stack,
    Divider,
    Breadcrumbs,
    Link as MuiLink,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';

// --- Icons ---
import {
    Visibility,
    AccessTime,
    Speed,
    LocalGasStation,
    PowerSettingsNew,
    ChevronRight,
    Search,
    CalendarToday,
    ArrowBack,
    PictureAsPdf,
    TableView
} from '@mui/icons-material';

// --- Services ---
import apiClient from '../../services/apiClient';
import rentalService from '../../services/rentalService';

/**
 * LogPage Component
 * 
 * The main container for the log exploration interface.
 */

// Built-in SPN → readable name fallback (mirrors the spn_mapping DB table).
// Used so numbered CAN columns always show a name even if the live map is empty.
const SPN_NAMES = {
  '100': 'Engine Oil Pressure', '110': 'Coolant Temp', '190': 'Engine Speed',
  '200': 'Engine Fuel Rate', '247': 'Engine Working Hours', '250': 'Engine Total Fuel Used',
  '271': 'Main Boom Angle', '272': 'Main Boom Angle', '500': 'SOC',
  '510': 'Traction Motor Speed', '511': 'Traction Motor Temperature', '512': 'Traction Motor Current',
  '513': 'Actual Torque %', '600': 'Pump Motor Speed', '601': 'Pump Motor Temperature',
  '602': 'Pump Motor Current', '603': 'Pump Motor Request Speed',
  '700': 'Control Battery Voltage', '710': 'System Battery Voltage', '898': 'Engine Request Speed',
  '972': 'Turret Swing Angle', '1271': 'Lower Boom to Turntable Angle', '1272': 'Lower Boom Extend Length',
  '1273': 'Turntable Angle Y', '2700': 'Main Boom Angle', '2702': 'Main Boom Extend Length',
  '2703': 'Jib Angle', '2720': 'Cage Angle', '2721': 'Chassis Tilt Angle X',
  '2723': 'Chassis Tilt Angle Y', '2724': 'Cage Load', '2750': 'Hydraulic Oil Temperature',
  '2751': 'Fuel Level', '2755': 'Loadchart', '2756': 'Cage Swing Angle'
};

const LogPage = () => {
    // --- 1. Hooks & Routing ---
    const navigate = useNavigate();
    const origin = getContractOrigin();   // the door this contract was opened from

    // Get the rentalId from the URL path (e.g. /logs/EQ-B001)
    const { rentalId } = useParams();

    // Get query parameters (e.g. ?date=2023-01-01)
    const [searchParams, setSearchParams] = useSearchParams();

    // --- 2. State Management ---

    // View Mode: 'list' = show dates, 'detail' = show specific logs
    const [viewMode, setViewMode] = useState('list');

    // The currently active date for analysis
    const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || null);

    // The rental contract this log was reached through — used to build a full breadcrumb trail.
    // Prefer the ?contractId=... URL param (passed when navigating from RentalDetailsPage);
    // otherwise we fill it in from getRentalDetail() once it returns.
    const [contractId, setContractId] = useState(searchParams.get('contractId') || null);

    // Data Storage
    const [logDates, setLogDates] = useState([]); // Array of available dates
    const [logs, setLogs] = useState([]); // Array of log entries for the selected date
    const [deviceInfo, setDeviceInfo] = useState(null); // Basic info about the machine
    const [spnMap, setSpnMap] = useState({}); // { '247': { name, unit } } — keyed by SPN number

    // Loading States
    const [loading, setLoading] = useState(true); // Initial page load
    const [loadingLogs, setLoadingLogs] = useState(false); // Fetching detailed logs

    // Calculated stats for the selected day
    const [dailySummary, setDailySummary] = useState(null);

    // Search/Filter inputs
    const [dateSearchTerm, setDateSearchTerm] = useState('');
    const [logSearchTerm, setLogSearchTerm] = useState('');

    // --- 3. Data Fetching Effects ---

    // Effect: Initial Load (Run once when page opens)
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                // Fetch available dates, device details, and recent history logs for summarization
                const [datesRes, deviceRes, historyRes] = await Promise.all([
                    rentalService.getLogDates(rentalId),
                    rentalService.getRentalDetail(rentalId),
                    rentalService.getRentalHistory(rentalId, 5000) // Increase limit to cover summaries for more dates
                ]);

                // 1. Group history by date (stored as YYYY-MM-DD for grouping)
                const historyByDate = new Map();
                if (historyRes && historyRes.history) {
                    historyRes.history.forEach(log => {
                        const d = new Date(log.recorded_at || log.timestamp);
                        const dStr = d.toISOString().split('T')[0];
                        if (!historyByDate.has(dStr)) historyByDate.set(dStr, []);
                        historyByDate.get(dStr).push(log);
                    });
                }

                // 2. Process Dates with Summaries
                if (datesRes && datesRes.dates) {
                    const enrichedDates = datesRes.dates.map(dateItem => {
                        const logs = historyByDate.get(dateItem.date) || [];
                        if (logs.length === 0) return dateItem;

                        // Sort chronological (oldest to newest) for scanning
                        const sortedLogs = [...logs].sort((a, b) => new Date(a.recorded_at || a.timestamp) - new Date(b.recorded_at || b.timestamp));

                        // Summarization metrics
                        let firstOn = null, lastOff = null;
                        let startEng = 0, endEng = 0;
                        let fuelUsed = 0, prevFuel = null;

                        sortedLogs.forEach(l => {
                            const raw = typeof l.payload === 'string' ? JSON.parse(l.payload) : (l.payload || l);
                            const data = {};
                            Object.entries(raw || {}).forEach(([k, v]) => {
                                data[k.toLowerCase().replace(/[\s_]/g, '')] = v;
                            });

                            const get = (...ks) => {
                                for (const k of ks) {
                                    const val = data[k.toLowerCase().replace(/[\s_]/g, '')];
                                    if (val !== undefined && val !== null && val !== '') return parseFloat(val);
                                }
                                return null;
                            };

                            // SPN 247=Engine Hours, 190=Engine Speed, 2751=Fuel Level
                            const ewh = get('247', 'engine_working_hours', 'engine_hours', 'total_hours');
                            const espeed = get('190', 'engine_speed', 'engine_rpm');
                            const f = get('2751', 'fuel_level', 'adc_level', 'Engine_Total_Fuel_Used');
                            const time = l.recorded_at || l.timestamp;

                            if (ewh > 0 || espeed > 0) {
                                if (!firstOn) {
                                    firstOn = time;
                                    startEng = ewh || 0;
                                }
                                lastOff = time;
                                endEng = ewh || 0;
                            }

                            if (f !== null && f > 0) {
                                if (prevFuel !== null && f < prevFuel) fuelUsed += (prevFuel - f);
                                prevFuel = f;
                            }
                        });

                        let opHrs = 0;
                        if (firstOn && lastOff) {
                            opHrs = Math.max(0, (new Date(lastOff).getTime() - new Date(firstOn).getTime()) / (1000 * 60 * 60));
                        }

                        return {
                            ...dateItem,
                            summary: {
                                firstOn: firstOn ? new Date(firstOn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null,
                                lastOff: lastOff ? new Date(lastOff).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null,
                                opHours: opHrs,
                                engineHours: Math.max(0, endEng - startEng),
                                fuel: fuelUsed
                            }
                        };
                    });
                    setLogDates(enrichedDates);
                }

                // 3. Asset Identity
                if (deviceRes && deviceRes.identity) {
                    setDeviceInfo({
                        name: deviceRes.identity.name,
                        product_category: deviceRes.identity.category,
                        device_type: deviceRes.status.current,
                        location: { lat: deviceRes.contract.location, lon: '' }
                    });
                }

                // 4. Capture contract id (only if not already passed via URL) so breadcrumb shows it
                if (!contractId && deviceRes?.contract?.id && deviceRes.contract.id !== '-') {
                    setContractId(deviceRes.contract.id);
                }
            } catch (err) {
                console.error("Failed to fetch initial log data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (rentalId) {
            fetchInitialData();
        }
    }, [rentalId]);

    // Effect: Handle View Changes based on selectedDate
    useEffect(() => {
        if (selectedDate) {
            // If a date is selected, switch to Detail Mode and fetch data
            setViewMode('detail');
            fetchLogsForDate(selectedDate);
        } else {
            // If no date, show the List Mode
            setViewMode('list');
            setLogs([]);
        }
    }, [selectedDate]);

    // --- 4. Logic Functions ---

    /**
     * fetchLogsForDate
     * Purpose: Downloads the full history for a single day.
     * @param {string} date - YYYY-MM-DD
     */
    const fetchLogsForDate = async (date) => {
        try {
            setLoadingLogs(true);
            // Limit to 1000 records to prevent browser crashing on huge days
            const res = await rentalService.getRentalHistory(rentalId, 1000, date);

            if (res && res.history) {
                // Capture SPN mapping returned alongside history (if present)
                const liveSpnMap = res.spn_mapping || spnMap || {};
                if (res.spn_mapping) setSpnMap(res.spn_mapping);

                // Process the raw logs into a display-friendly format
                const formatted = res.history.map((log, index) => {
                    const { timestamp, device_id, ...rest } = log;

                    // Flatten parameters into a key-value list, translating SPN numeric keys to names
                    const fields = Object.entries(rest)
                        .filter(([key, value]) => value !== null && value !== undefined && value !== '')
                        .map(([key, value]) => {
                            const mapped = liveSpnMap[key];
                            const label = (mapped && mapped.name)
                                ? mapped.name.toUpperCase()
                                : (SPN_NAMES[key]
                                    ? SPN_NAMES[key].toUpperCase()
                                    : key.replace(/_/g, ' ').toUpperCase());
                            return {
                                key: label,
                                value: typeof value === 'object' ? JSON.stringify(value) : String(value)
                            };
                        });

                    return {
                        id: log.id || index,
                        time: new Date(timestamp).toLocaleTimeString('en-GB', { hour12: false }),
                        fullTime: timestamp,
                        fields
                    };
                });

                setLogs(formatted);
                // After fetching, calculate the summary
                calculateDailySummary(res.history);
            }
        } catch (err) {
            console.error("Failed to fetch logs for date:", err);
        } finally {
            setLoadingLogs(false);
        }
    };

    /**
     * calculateDailySummary
     * Purpose: Analyses the days logs to find totals (Hours run, Fuel used).
     * Logic: Compares the first log of the day vs the last log of the day.
     */
    const calculateDailySummary = (rawLogs) => {
        if (!rawLogs || rawLogs.length === 0) {
            setDailySummary(null);
            return;
        }

        const lastLog = rawLogs[0]; // Newest record (End of day)

        // Helper to extract a number safely from potentially different field names and payload nesting
        const getNumericValue = (log, ...keys) => {
            const data = typeof log.payload === 'string' ? JSON.parse(log.payload) : (log.payload || log);
            const normalizedData = {};
            if (data && typeof data === 'object') {
                for (const [k, v] of Object.entries(data)) {
                    normalizedData[k.toLowerCase().replace(/[\s_]/g, '')] = v;
                }
            }
            const normalizedKeys = keys.map(k => k.toLowerCase().replace(/[\s_]/g, ''));
            for (const key of normalizedKeys) {
                const val = normalizedData[key];
                if (val !== null && val !== undefined && val !== '') {
                    const num = parseFloat(val);
                    if (!isNaN(num)) return num;
                }
            }
            return 0;
        };

        // 1. Identify Activity Window (Key ON / Key OFF)
        let firstOnTime = null;
        let lastOffTime = null;
        let startEngMeter = 0;
        let endEngMeter = 0;

        // Logs are DESC (newest first). Scan chronological (oldest to newest)
        for (let i = rawLogs.length - 1; i >= 0; i--) {
            const l = rawLogs[i];
            // SPN 247=Engine Hours, 190=Engine Speed
            const ewh = getNumericValue(l, '247', 'engine_working_hours', 'engine_hours', 'total_hours', 'Engine_Working_Hours');
            const espeed = getNumericValue(l, '190', 'engine_speed', 'engine_rpm', 'Engine_Speed');
            const time = l.recorded_at || l.timestamp;

            if (ewh > 0 || espeed > 0) {
                if (!firstOnTime) {
                    firstOnTime = time;
                    startEngMeter = ewh;
                }
                lastOffTime = time;
                endEngMeter = ewh;
            }
        }

        // 2. Calculations
        let opHours = 0;
        if (firstOnTime && lastOffTime) {
            opHours = Math.max(0, (new Date(lastOffTime).getTime() - new Date(firstOnTime).getTime()) / (1000 * 60 * 60));
        }
        const engineHours = Math.max(0, endEngMeter - startEngMeter);

        // 3. Fuel Calculation (Drop Sum for better accuracy)
        let totalFuelUsed = 0;
        let prevFuel = null;
        for (let i = rawLogs.length - 1; i >= 0; i--) {
            // SPN 2751=Fuel Level (%)
            const f = getNumericValue(rawLogs[i], '2751', 'fuel_level', 'adc_level', 'Engine_Total_Fuel_Used');
            if (f > 0) {
                if (prevFuel !== null && f < prevFuel) {
                    totalFuelUsed += (prevFuel - f);
                }
                prevFuel = f;
            }
        }

        const totalDistance = getNumericValue(lastLog, 'distance', 'total_distance');

        // Extract Alarms
        const alarms = [];
        rawLogs.forEach(log => {
            Object.keys(log).forEach(key => {
                if (key.toLowerCase().includes('alarm') || key.toLowerCase().includes('alert') || key.toLowerCase().includes('warning')) {
                    const value = log[key];
                    if (value && value !== '0' && value !== 0 && value !== 'false' && value !== false) {
                        alarms.push({ parameter: key, value: String(value) });
                    }
                }
            });
        });

        setDailySummary({
            firstOn: firstOnTime ? new Date(firstOnTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null,
            lastOff: lastOffTime ? new Date(lastOffTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null,
            operationalHours: opHours,
            engineHours: engineHours,
            fuel: totalFuelUsed,
            distance: totalDistance,
            alarmCount: alarms.length,
            alarms: alarms.slice(0, 5)
        });
    };

    // --- 5. Event Handlers ---

    const handleSelectDate = (date) => {
        setSearchParams({ date }); // Update URL so page is shareable
        setSelectedDate(date);
    };

    const handleBackToList = () => {
        setSearchParams({}); // Clear URL
        setSelectedDate(null);
    };

    const handleDownload = async (type) => {
        const deviceId = rentalId;
        try {
            const endpoint = `/downloads/device-logs/${type}/${deviceId}?date=${selectedDate}`;
            const { blob, filename } = await apiClient.download(endpoint);

            // Create temporary link to trigger browser download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    // --- 6. Derived Display Data ---

    // Filter dates logic
    const filteredDates = logDates.filter(d => d.date.includes(dateSearchTerm));

    // Prepare Header Info
    const displayInfo = {
        name: deviceInfo?.name || 'Unknown Device',
        category: deviceInfo?.product_category || 'MEWP',
        image: '/images/mewp_machine.png',
        status: deviceInfo?.device_type || 'Active',
        contractId: 'Contract Info Unavailable',
        customer: 'Customer Info Unavailable',
        location: deviceInfo?.location ? `${deviceInfo.location.lat}, ${deviceInfo.location.lon}` : 'Unknown Location'
    };

    // --- 7. Main Render ---
    return (
        <Box p={3} className="animate-fade-in">
            {/* Breadcrumbs Navigation: Rentals → ContractID → equipment_id → Logs */}
            <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 3 }}>
                <MuiLink component="button" onClick={() => navigate(origin.path)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>{origin.label}</MuiLink>
                {contractId && (
                    <MuiLink
                        component="button"
                        onClick={() => navigate(`/contract-live/${contractId}`)}
                        underline="hover"
                        color="inherit"
                        sx={{ fontWeight: 600 }}
                    >
                        {contractId}
                    </MuiLink>
                )}
                <MuiLink
                    component="button"
                    onClick={() => navigate(`/rental-details/${rentalId}${contractId ? `?contractId=${contractId}` : ''}`)}
                    underline="hover"
                    color="inherit"
                    sx={{ fontWeight: 600 }}
                >
                    {rentalId}
                </MuiLink>
                <Typography color="text.primary" sx={{ fontWeight: 700 }}>Logs</Typography>
            </Breadcrumbs>

            {/* 
               VIEW SWITCHER 
               If viewMode is 'list': Show available dates.
               If viewMode is 'detail': Show logs for selected date.
            */}
            {viewMode === 'list' ? (
                /* --- LIST VIEW --- */
                <Card sx={{ borderRadius: '24px' }}>
                    <Box p={3} borderBottom="1px solid" borderColor="divider" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h6" fontWeight={800}>Available Logs</Typography>
                            <Typography variant="body2" color="text.secondary">Select a date to view detailed time-wise logs</Typography>
                        </Box>
                        {/* ... (Search Field kept same) ... */}
                        <TextField
                            size="small"
                            placeholder="Search by date (YYYY-MM-DD)..."
                            value={dateSearchTerm}
                            onChange={(e) => setDateSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                                sx: { borderRadius: '12px', bgcolor: 'grey.50' }
                            }}
                        />
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>DATE</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>ON / OFF TIMES</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>WORKING (HRS)</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>FUEL (L)</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>LOGS</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '12px' }}>ACTION</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>Loading dates...</TableCell>
                                    </TableRow>
                                )}
                                {!loading && filteredDates.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>No logs found.</TableCell>
                                    </TableRow>
                                )}
                                {filteredDates.map((item) => (
                                    <TableRow key={item.date} hover onClick={() => handleSelectDate(item.date)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Avatar sx={{ bgcolor: 'primary.100', color: 'primary.main', borderRadius: '10px', width: 38, height: 38 }} variant="rounded">
                                                    <CalendarToday fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={800} color="text.primary">
                                                        {new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                        {new Date(item.date).toLocaleDateString('en-GB', { weekday: 'long' })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* ON / OFF */}
                                        <TableCell>
                                            {item.summary?.firstOn ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        icon={<PowerSettingsNew sx={{ fontSize: '12px !important' }} />}
                                                        label={item.summary.firstOn}
                                                        size="small"
                                                        sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 800, height: 24, fontSize: '11px' }}
                                                    />
                                                    <Chip
                                                        label={item.summary.lastOff}
                                                        size="small"
                                                        sx={{ bgcolor: 'error.50', color: 'error.main', fontWeight: 800, height: 24, fontSize: '11px' }}
                                                    />
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" fontWeight={700}>-</Typography>
                                            )}
                                        </TableCell>

                                        {/* WORKING HOURS */}
                                        <TableCell>
                                            {item.summary ? (
                                                <Box>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <AccessTime sx={{ fontSize: 14, color: 'primary.main' }} />
                                                        <Typography variant="body2" fontWeight={800} color="primary.main">
                                                            {item.summary.opHours.toFixed(1)} <Typography component="span" variant="caption" fontWeight={700}>h</Typography>
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Speed sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                                            {item.summary.engineHours.toFixed(1)} h (Eng)
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ) : <Typography variant="caption" color="text.disabled" fontWeight={700}>-</Typography>}
                                        </TableCell>

                                        {/* FUEL */}
                                        <TableCell>
                                            {item.summary ? (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <LocalGasStation sx={{ fontSize: 14, color: 'success.main' }} />
                                                    <Typography variant="body2" fontWeight={800} color="success.main">
                                                        {item.summary.fuel > 0 ? `${item.summary.fuel.toFixed(1)} L` : '-'}
                                                    </Typography>
                                                </Stack>
                                            ) : <Typography variant="caption" color="text.disabled" fontWeight={700}>-</Typography>}
                                        </TableCell>

                                        {/* LOG ENTRIES */}
                                        <TableCell>
                                            <Chip label={`${item.count} Entries`} size="small" variant="outlined" sx={{ fontWeight: 700, borderRadius: '6px' }} />
                                        </TableCell>

                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                elevation={0}
                                                endIcon={<ChevronRight />}
                                                sx={{
                                                    fontWeight: 800,
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    '&:hover': { boxShadow: 'none' }
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

            ) : (
                /* --- DETAIL VIEW (Specific Date) --- */
                <Box>
                    <Button startIcon={<ArrowBack />} onClick={handleBackToList} sx={{ mb: 2, fontWeight: 700 }}>
                        Back to Log List
                    </Button>

                    {/* MERGED HEADER: Machine Identity + Daily Stats + Downloads */}
                    <Card sx={{ mb: 4, borderRadius: '24px', overflow: 'visible' }}>
                        <CardContent sx={{ p: '24px !important' }}>
                            <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">

                                {/* 1. Machine Identity (Left Side) */}
                                <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 250 }}>
                                    <Box sx={{ width: 64, height: 64, bgcolor: 'grey.50', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider' }}>
                                        <img src={displayInfo.image} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="Device" onError={(e) => e.target.src = 'https://via.placeholder.com/80?text=IMG'} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{displayInfo.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>{displayInfo.category}</Typography>
                                        <Chip label={displayInfo.status} color="primary" size="small" sx={{ fontWeight: 800, borderRadius: '6px', height: '22px', fontSize: '11px' }} />
                                    </Box>
                                </Box>

                                <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                                {/* 2. Date & Context (Middle Left) */}
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">DATE VIEWING</Typography>
                                    <Typography variant="h6" fontWeight={800} lineHeight={1}>
                                        {new Date(selectedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </Typography>
                                    {dailySummary && dailySummary.firstOn && (
                                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            <span style={{ color: '#1976d2' }}>ON: {dailySummary.firstOn}</span> • <span style={{ color: '#d32f2f' }}>OFF: {dailySummary.lastOff}</span>
                                        </Typography>
                                    )}
                                </Box>

                                {/* 3. Daily Stats (Middle Right - Flexible) */}
                                {dailySummary && (
                                    <Box display="flex" alignItems="center" gap={4} flexGrow={1} justifyContent="center">
                                        {/* Operational Hours */}
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">DAILY WORKING</Typography>
                                            <Typography variant="h6" fontWeight={800} color="primary.main" title="Wallclock time from first activity to last">
                                                {dailySummary.operationalHours.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>h</Typography>
                                            </Typography>
                                        </Box>

                                        {/* Engine Hours */}
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ENGINE HRS</Typography>
                                            <Typography variant="h6" fontWeight={800} color="info.main" title="Actual engine runtime delta">
                                                {dailySummary.engineHours.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>h</Typography>
                                            </Typography>
                                        </Box>

                                        {/* Fuel */}
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">FUEL CONSUMED</Typography>
                                            <Typography variant="h6" fontWeight={800} color="success.main">
                                                {dailySummary.fuel > 0 ? dailySummary.fuel.toFixed(1) : '-'} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>L</Typography>
                                            </Typography>
                                        </Box>

                                        {/* Alarms */}
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ALARMS</Typography>
                                            <Typography variant="h6" fontWeight={800} color={dailySummary.alarmCount > 0 ? 'error.main' : 'text.primary'}>
                                                {dailySummary.alarmCount}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {!dailySummary && <Box flexGrow={1} />}

                                {/* 4. Downloads (Far Right) */}
                                <Box display="flex" flexDirection="column" gap={0.5} sx={{ pl: 2, borderLeft: 1, borderColor: 'divider' }}>
                                    <Button
                                        size="small"
                                        startIcon={<PictureAsPdf sx={{ fontSize: 16 }} />}
                                        onClick={() => handleDownload('pdf')}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '11px',
                                            py: 0.5,
                                            minWidth: 80,
                                            color: 'error.main',
                                            bgcolor: 'error.50',
                                            '&:hover': { bgcolor: 'error.100' }
                                        }}
                                    >
                                        PDF
                                    </Button>
                                    <Button
                                        size="small"
                                        startIcon={<TableView sx={{ fontSize: 16 }} />}
                                        onClick={() => handleDownload('excel')}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '11px',
                                            py: 0.5,
                                            minWidth: 80,
                                            color: 'success.main',
                                            bgcolor: 'success.50',
                                            '&:hover': { bgcolor: 'success.100' }
                                        }}
                                    >
                                        Excel
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Detailed Log Entries List */}
                    <Card sx={{ borderRadius: '24px' }}>
                        <Box p={3} borderBottom="1px solid" borderColor="divider">
                            <TextField
                                size="small"
                                placeholder="Search logs..."
                                value={logSearchTerm}
                                onChange={(e) => setLogSearchTerm(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                                    sx: { borderRadius: '12px', bgcolor: 'grey.50' }
                                }}
                            />
                        </Box>
                        <Box p={3}>
                            <Stack spacing={2}>
                                {loadingLogs && <Typography align="center" py={4}>Loading logs...</Typography>}
                                {!loadingLogs && logs.length === 0 && <Typography align="center" py={4} color="text.secondary">No logs found for this date.</Typography>}

                                {logs
                                    .filter(l => JSON.stringify(l).toLowerCase().includes(logSearchTerm.toLowerCase()))
                                    .map((log) => (
                                        <Card key={log.id} variant="outlined" sx={{ borderRadius: '16px' }}>
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Chip
                                                        label={log.time}
                                                        size="small"
                                                        sx={{
                                                            borderRadius: '6px',
                                                            fontWeight: 700,
                                                            fontFamily: 'monospace',
                                                            bgcolor: 'grey.100'
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="text.disabled">{log.id}</Typography>
                                                </Box>
                                                <Grid container spacing={2}>
                                                    {log.fields.map((field, idx) => (
                                                        <Grid item xs={6} md={3} lg={2} key={idx}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="11px">
                                                                    {field.key}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '13px' }}>
                                                                    {field.value}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                            </Stack>
                        </Box>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default LogPage;
